<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-agency-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header -->
      <v-card-title class="editor-header d-flex justify-space-between align-center px-6 py-4">
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-link-variant</v-icon>
          Agency Links
        </div>
        <v-btn icon @click="onClose" class="close-btn" color="white" depressed large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center mb-2">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Add your agency's website and social media links
        </div>
        <div class="d-flex align-center text-body-2">
          <v-icon color="success" size="16" class="mr-2">mdi-check-circle</v-icon>
          <span class="text--secondary">
            URLs will be automatically formatted with https:// if needed
          </span>
        </div>
      </v-card-text>

      <!-- Error/Success message -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            icon="mdi-alert-circle"
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Inputs -->
      <v-card-text class="px-6 py-2">
        <v-row>
          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.website"
              label="Website*"
              outlined
              dense
              prepend-inner-icon="mdi-web"
              :rules="[rules.website]"
              @input="onChange"
              @blur="onUrlBlur('website')"
              class="modern-input"
              placeholder="example.com or https://example.com"
              hint="Enter your website URL (https:// will be added automatically)"
              persistent-hint
              :error="rules.website(profile.website) !== true"
            />
          </v-col>

          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.facebook"
              label="Facebook"
              outlined
              dense
              prepend-inner-icon="mdi-facebook"
              :rules="[rules.social]"
              @input="onChange"
              @blur="onUrlBlur('facebook')"
              class="modern-input"
              placeholder="facebook.com/yourpage or https://facebook.com/yourpage"
              hint="Enter your Facebook page URL (https:// will be added automatically)"
              persistent-hint
              :error="profile.facebook && rules.social(profile.facebook) !== true"
            />
          </v-col>

          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.twitter"
              label="Twitter"
              outlined
              dense
              prepend-inner-icon="mdi-twitter"
              :rules="[rules.social]"
              @input="onChange"
              @blur="onUrlBlur('twitter')"
              class="modern-input"
              placeholder="twitter.com/yourhandle or https://twitter.com/yourhandle"
              hint="Enter your Twitter profile URL (https:// will be added automatically)"
              persistent-hint
              :error="profile.twitter && rules.social(profile.twitter) !== true"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-center">
        <v-btn
          color="primary"
          :disabled="!canSave || loading"
          @click="onSave"
          depressed
          width="200"
          :loading="loading"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Links
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  props: {
    dialog: { type: Boolean, default: false },
    onClose: { type: Function }
  },
  data() {
    return {
      profile: {},
      message: null,
      messageType: "error",
      loading: false,
      rules: {
        website: value => {
          if (!value) return "Website is required";
          const formatted = value.trim();
          if (!/^https?:\/\//.test(formatted)) return "Please include https:// or http://";
          const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
          return urlPattern.test(formatted) || "Please enter a valid URL (e.g., https://example.com)";
        },
        social: value => {
          if (!value) return true; // optional
          const formatted = value.trim();
          if (!/^https?:\/\//.test(formatted)) return "Please include https:// or http://";
          const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
          return urlPattern.test(formatted) || "Please enter a valid URL";
        }
      }
    };
  },
  computed: {
    ...mapState("auth", { myProfile: "profile" }),
    // Determine if Save button should be enabled
    canSave() {
      return (
        this.profile.website &&
        this.rules.website(this.profile.website) === true &&
        (!this.profile.facebook || this.rules.social(this.profile.facebook) === true) &&
        (!this.profile.twitter || this.rules.social(this.profile.twitter) === true)
      );
    }
  },
  watch: {
    myProfile(newVal) {
      this.profile = { ...newVal };
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
  },
  methods: {
    ...mapActions("auth", { updateProfile: "updateProfile" }),
    onChange() { this.message = null; },
    formatUrl(url) {
      if (!url) return "";
      url = url.trim();
      if (!/^https?:\/\//.test(url)) url = "https://" + url;
      return url;
    },
    onUrlBlur(field) {
      if (this.profile[field]) this.profile[field] = this.formatUrl(this.profile[field]);
    },
    onSave() {
      this.message = null;
      const formattedProfile = {
        website: this.formatUrl(this.profile.website),
        facebook: this.formatUrl(this.profile.facebook),
        twitter: this.formatUrl(this.profile.twitter)
      };

      // Validate again before saving
      if (this.rules.website(formattedProfile.website) !== true) {
        this.message = this.rules.website(formattedProfile.website);
        this.messageType = "error";
        return;
      }
      if (formattedProfile.facebook && this.rules.social(formattedProfile.facebook) !== true) {
        this.message = this.rules.social(formattedProfile.facebook);
        this.messageType = "error";
        return;
      }
      if (formattedProfile.twitter && this.rules.social(formattedProfile.twitter) !== true) {
        this.message = this.rules.social(formattedProfile.twitter);
        this.messageType = "error";
        return;
      }

      // Save
      this.loading = true;
      this.updateProfile(formattedProfile)
        .then(() => {
          this.loading = false;
          this.message = "Links saved successfully!";
          this.messageType = "success";
          this.profile = { ...this.profile, ...formattedProfile };
          setTimeout(() => this.onClose(), 1500);
        })
        .catch(err => {
          this.loading = false;
          this.messageType = "error";
          this.message = err.response?.data?.message || "An error occurred";
        });
    }
  }
};
</script>

<style scoped>
.modern-agency-dialog { border-radius: 16px; overflow: hidden; }

.editor-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}
.close-btn { transition: all 0.3s ease; }
.close-btn:hover { transform: rotate(90deg); background: rgba(255,255,255,0.2); }

.modern-input::v-deep .v-input__slot {
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.25,0.8,0.5,1);
  background-color: white;
}
.save-btn {
  box-shadow: 0 4px 12px rgba(58,123,213,0.3);
  transition: all 0.3s ease;
}
.save-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(58,123,213,0.4); }
.save-btn:active { transform: translateY(0); }

.custom-loader { animation: loader 1s infinite; display: flex; }
@keyframes loader { from { transform: rotate(0); } to { transform: rotate(360deg); } }

.modern-agency-dialog::-webkit-scrollbar { width: 8px; height: 8px; }
.modern-agency-dialog::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
.modern-agency-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}
.modern-agency-dialog::-webkit-scrollbar-thumb:hover { background: #3a7bd5; }

/* Icon colors */
.modern-input::v-deep .mdi-facebook { color: #1877F2; }
.modern-input::v-deep .mdi-twitter { color: #1DA1F2; }
.modern-input::v-deep .mdi-web { color: #4CAF50; }
</style>
